import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Calendar, Upload, X } from 'lucide-react';
import { UploadFile } from '@/integrations/Core';

const paymentModes = [
    { value: 'virement', label: 'Virement bancaire' },
    { value: 'cheque', label: 'Chèque' },
    { value: 'especes', label: 'Espèces' },
    { value: 'materiel_equipement', label: 'Matériel/Équipement' }
];

// @ts-nocheck
export default function EncaissementForm({ isOpen, onClose, onSubmit, projectCurrency, maxAmount }) {
    const [isUploading, setIsUploading] = React.useState(false);
    const [uploadedFile, setUploadedFile] = React.useState(null);

    const { register, handleSubmit, control, formState: { errors }, reset } = useForm({
        defaultValues: {
            date: new Date().toISOString().split('T')[0],
            payment_mode: 'virement',
            amount: '',
            comment: '',
            document_url: ''
        }
    });

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const { file_url } = await UploadFile({ file });
            setUploadedFile({ name: file.name, url: file_url });
        } catch (error) {
            console.error('Erreur upload:', error);
        } finally {
            setIsUploading(false);
        }
    };

    const onSubmitForm = async (data) => {
        const encaissementData = {
            ...data,
            amount: parseFloat(data.amount),
            document_url: uploadedFile?.url || '',
            id: Math.random().toString(36).substr(2, 9)
        };

        await onSubmit(encaissementData);
        reset();
        setUploadedFile(null);
    };

    const currencySymbol = { EUR: '€', USD: '$', MAD: 'DH' }[projectCurrency] || projectCurrency;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Nouvel encaissement</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
                    <div>
                        <Label htmlFor="date">Date d'encaissement</Label>
                        <Input
                            {...register("date", { required: "Date obligatoire" })}
                            type="date"
                        />
                        {errors.date && <p className="text-red-500 text-sm">{errors.date.message}</p>}
                    </div>

                    <div>
                        <Label htmlFor="payment_mode">Mode de paiement</Label>
                        <Controller
                            name="payment_mode"
                            control={control}
                            render={({ field }) => (
                                <Select value={field.value} onValueChange={field.onChange}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {paymentModes.map(mode => (
                                            <SelectItem key={mode.value} value={mode.value}>
                                                {mode.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        />
                    </div>

                    <div>
                        <Label htmlFor="amount">Montant encaissé ({currencySymbol})</Label>
                        <Input
                            {...register("amount", {
                                required: "Montant obligatoire",
                                min: { value: 0.01, message: "Montant doit être positif" },
                                max: { value: maxAmount, message: `Montant ne peut dépasser ${maxAmount} ${currencySymbol}` }
                            })}
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                        />
                        {errors.amount && <p className="text-red-500 text-sm">{errors.amount.message}</p>}
                    </div>

                    <div>
                        <Label>Document justificatif (optionnel)</Label>
                        <div className="mt-2">
                            {uploadedFile ? (
                                <div className="flex items-center justify-between p-2 border rounded">
                                    <span className="text-sm">{uploadedFile.name}</span>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setUploadedFile(null)}
                                    >
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                        onChange={handleFileUpload}
                                        disabled={isUploading}
                                    />
                                    {isUploading && <span className="text-sm text-gray-500">Upload...</span>}
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="comment">Commentaire</Label>
                        <Textarea
                            {...register("comment")}
                            placeholder="Commentaire optionnel..."
                            rows={2}
                        />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Annuler
                        </Button>
                        <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                            Enregistrer l'encaissement
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
